
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from './Layouts/Header';
import Footer from './Layouts/Footer';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getCart, updateCart, removeFromCart } from '../utils/apiService';

const Cart = () => {
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingItems, setUpdatingItems] = useState({});
    const [taxRate, setTaxRate] = useState(0);
    const [currency, setCurrency] = useState('$'); // default fallback
    const navigate = useNavigate();
    const token = localStorage.getItem('token');

    const fetchCartData = async () => {
        if (!token) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const result = await getCart(token);
            console.log('Cart API Response:', result); // Debug log
            if (result && result.status) {
                // The API might return an array or an object with an items array
                const cartData = result.data || [];
                const items = Array.isArray(cartData) ? cartData : (cartData.items && Array.isArray(cartData.items) ? cartData.items : []);
                console.log('Processed cart items:', items); // Debug log
                setCartItems(items);
                if (cartData.tax_rate !== undefined) {
                    setTaxRate(Number(cartData.tax_rate));
                }
                
                if (cartData.currency) {
                    setCurrency(cartData.currency);
                }
            }
        } catch (error) {
            console.error('Error fetching cart:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!token) {
            navigate('/login');
            return;
        }
        fetchCartData();
    }, [token, navigate]);

    useLayoutEffect(() => {
        if (loading) return;
        const timer = setTimeout(() => {
            if (window.animationText) window.animationText();
            if (window.scrollingEffect) window.scrollingEffect();
            if (window.animateImgItem) window.animateImgItem();

            if (window.ScrollTrigger) {
                window.ScrollTrigger.refresh();
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            if (window.revertAnimations) window.revertAnimations();
            if (window.revertHeadline) window.revertHeadline();
        };
    }, [loading, cartItems]);

    const getImageUrl = (imgVal) => {
        if (!imgVal) return '';
        const imgStr = imgVal.toString().trim();
        if (imgStr.startsWith('http') || imgStr.startsWith('data:') || imgStr.startsWith('/')) {
            return imgStr;
        }
        return `${process.env.REACT_APP_STORAGE_BASE_URL || 'https://wmk.govindcrankrod.com/storage'}/${imgStr}`;
    };

    const handleQuantityChange = async (item, newQty) => {
        if (newQty < 1) return;

        const productId = item.product_id || item.id;
        const itemKey = item.product_id || item.id; // Use consistent key for tracking
        console.log('Updating cart for product:', productId, 'New quantity:', newQty); // Debug log

        setUpdatingItems(prev => ({ ...prev, [itemKey]: true }));
        try {
            const result = await updateCart({ productId: productId, quantity: newQty }, token);
            console.log('Update cart response:', result); // Debug log

            if (result && result.status) {
                // Refetch cart data to ensure consistency with backend
                await fetchCartData();
                toast.success('Quantity updated successfully');
            } else {
                toast.error(result.message || 'Failed to update quantity');
            }
        } catch (error) {
            console.error('Error updating quantity:', error);
            toast.error('Failed to update quantity');
        } finally {
            setUpdatingItems(prev => ({ ...prev, [itemKey]: false }));
        }
    };

    const handleRemoveItem = async (item) => {
        const swalResult = await Swal.fire({
            title: 'Remove Item?',
            html: `
                <div style="padding: 10px 0;">
                    <p style="font-size: 16px; color: #ccc; margin: 0;">
                        Are you sure you want to remove this item from your cart?
                    </p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: '<i class="fa fa-trash"></i> Yes, Remove',
            cancelButtonText: '<i class="fa fa-times"></i> Cancel',
            background: '#1a1a1a',
            color: '#fff',
            width: '550px',
            padding: '3em',
            customClass: {
                popup: 'custom-swal-popup',
                title: 'custom-swal-title',
                htmlContainer: 'custom-swal-text',
                confirmButton: 'custom-swal-confirm',
                cancelButton: 'custom-swal-cancel',
                icon: 'custom-swal-icon',
                actions: 'custom-swal-actions'
            },
            buttonsStyling: false,
            backdrop: 'rgba(0, 0, 0, 0.8)',
            willOpen: () => {
                // Add custom styles
                const style = document.createElement('style');
                style.innerHTML = `
                    .custom-swal-popup {
                        border-radius: 16px !important;
                        border: 1px solid rgba(255, 255, 255, 0.1);
                        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5) !important;
                    }
                    
                    .custom-swal-title {
                        font-size: 28px !important;
                        font-weight: 700 !important;
                        padding: 0 0 15px 0 !important;
                        color: #fff !important;
                    }
                    
                    .custom-swal-text {
                        font-size: 16px !important;
                        padding: 0 !important;
                        margin: 0 0 30px 0 !important;
                    }
                    
                    .custom-swal-icon {
                        width: 90px !important;
                        height: 90px !important;
                        border-width: 5px !important;
                        margin: 0 auto 25px !important;
                    }
                    
                    .custom-swal-icon.swal2-warning {
                        border-color: #facea8 !important;
                        color: #f8bb86 !important;
                    }
                    
                    .custom-swal-actions {
                        gap: 15px !important;
                        margin-top: 30px !important;
                    }
                    
                    .custom-swal-confirm {
                        background: linear-gradient(135deg, #dc3545 0%, #c82333 100%) !important;
                        color: white !important;
                        border: none !important;
                        padding: 14px 40px !important;
                        font-size: 16px !important;
                        font-weight: 600 !important;
                        border-radius: 8px !important;
                        cursor: pointer !important;
                        transition: all 0.3s ease !important;
                        box-shadow: 0 4px 15px rgba(220, 53, 69, 0.3) !important;
                    }
                    
                    .custom-swal-confirm:hover {
                        background: linear-gradient(135deg, #c82333 0%, #bd2130 100%) !important;
                        transform: translateY(-2px) !important;
                        box-shadow: 0 6px 20px rgba(220, 53, 69, 0.4) !important;
                    }
                    
                    .custom-swal-confirm:active {
                        transform: translateY(0) !important;
                    }
                    
                    .custom-swal-cancel {
                        background: rgba(108, 117, 125, 0.15) !important;
                        color: #adb5bd !important;
                        border: 1px solid rgba(108, 117, 125, 0.3) !important;
                        padding: 14px 40px !important;
                        font-size: 16px !important;
                        font-weight: 600 !important;
                        border-radius: 8px !important;
                        cursor: pointer !important;
                        transition: all 0.3s ease !important;
                    }
                    
                    .custom-swal-cancel:hover {
                        background: rgba(108, 117, 125, 0.25) !important;
                        border-color: rgba(108, 117, 125, 0.5) !important;
                        color: #fff !important;
                        transform: translateY(-2px) !important;
                    }
                    
                    .custom-swal-cancel:active {
                        transform: translateY(0) !important;
                    }
                    
                    .custom-swal-confirm i,
                    .custom-swal-cancel i {
                        margin-right: 8px;
                    }
                `;
                document.head.appendChild(style);
            }
        });

        if (!swalResult.isConfirmed) return;

        // Use product_id from the cart item
        const productId = item.product_id || item.id;
        const itemKey = item.product_id || item.id;
        console.log('Removing product:', productId); 

        setUpdatingItems(prev => ({ ...prev, [itemKey]: true }));
        try {
            const result = await removeFromCart(productId, token);
            console.log('Remove cart response:', result);

            if (result && result.status) {
                await fetchCartData();
                
                window.dispatchEvent(new CustomEvent('cartUpdated'));
                
                Swal.fire({
                    title: 'Item Removed!',
                    html: `
                        <div style="padding: 10px 0;">
                            <p style="font-size: 16px; color: #ccc; margin: 0;">
                                The item has been successfully removed from your cart.
                            </p>
                        </div>
                    `,
                    icon: 'success',
                    background: '#1a1a1a',
                    color: '#fff',
                    confirmButtonText: '<i class="fa fa-check"></i> Got it',
                    timer: 3000,
                    width: '500px',
                    padding: '2.5em',
                    customClass: {
                        popup: 'custom-success-popup',
                        title: 'custom-success-title',
                        htmlContainer: 'custom-success-text',
                        confirmButton: 'custom-success-confirm',
                        icon: 'custom-success-icon'
                    },
                    buttonsStyling: false,
                    backdrop: 'rgba(0, 0, 0, 0.8)',
                    showClass: {
                        popup: 'animate__animated animate__fadeInDown animate__faster'
                    },
                    hideClass: {
                        popup: 'animate__animated animate__fadeOutUp animate__faster'
                    },
                    willOpen: () => {
                        const style = document.createElement('style');
                        style.innerHTML = `
                            .custom-success-popup {
                                border-radius: 16px !important;
                                border: 1px solid rgba(40, 167, 69, 0.2);
                                box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5) !important;
                            }
                            
                            .custom-success-title {
                                font-size: 26px !important;
                                font-weight: 700 !important;
                                padding: 0 0 15px 0 !important;
                                color: #28a745 !important;
                            }
                            
                            .custom-success-text {
                                font-size: 16px !important;
                                padding: 0 !important;
                                margin: 0 0 25px 0 !important;
                            }
                            
                            .custom-success-icon {
                                width: 85px !important;
                                height: 85px !important;
                                border-width: 5px !important;
                                margin: 0 auto 20px !important;
                            }
                            
                            .custom-success-icon.swal2-success {
                                border-color: #28a745 !important;
                            }
                            
                            .custom-success-icon.swal2-success .swal2-success-ring {
                                border-color: rgba(40, 167, 69, 0.3) !important;
                            }
                            
                            .custom-success-icon.swal2-success [class^='swal2-success-line'] {
                                background-color: #28a745 !important;
                            }
                            
                            .custom-success-confirm {
                                background: linear-gradient(135deg, #28a745 0%, #218838 100%) !important;
                                color: white !important;
                                border: none !important;
                                padding: 12px 35px !important;
                                font-size: 16px !important;
                                font-weight: 600 !important;
                                border-radius: 8px !important;
                                cursor: pointer !important;
                                transition: all 0.3s ease !important;
                                box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3) !important;
                            }
                            
                            .custom-success-confirm:hover {
                                background: linear-gradient(135deg, #218838 0%, #1e7e34 100%) !important;
                                transform: translateY(-2px) !important;
                                box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4) !important;
                            }
                            
                            .custom-success-confirm:active {
                                transform: translateY(0) !important;
                            }
                            
                            .custom-success-confirm i {
                                margin-right: 8px;
                            }
                        `;
                        document.head.appendChild(style);
                    }
                });
            } else {
                toast.error(result.message || 'Failed to remove item');
            }
        } catch (error) {
            console.error('Error removing item:', error);
            toast.error('Failed to remove item');
        } finally {
            setUpdatingItems(prev => ({ ...prev, [itemKey]: false }));
        }
    };

    const subtotal = cartItems.reduce((acc, item) => acc + (Number(item.price) || 0) * (item.quantity || 0), 0);
    const tax = Math.round(subtotal * (taxRate / 100));
    const total = subtotal + tax;

    return (
        <div className="wrapper position-relative">
            <Header />

            <div className="page-title">
                <div className="tf-container">
                    <div className="page-title-content text-center">
                        <h1 className="title">
                            <span className="split-text effect-right">Shopping Cart</span>
                        </h1>
                        <div className="breadkcum">
                            <Link to="/" className="link-breadkcum body-2 fw-7">
                                <span className="split-text effect-right">Home</span>
                            </Link>
                            <span className="dot"></span>
                            <span className="page-breadkcum body-2 fw-7">
                                <span className="split-text effect-right">Cart</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="main-content">
                <section className="section-team p-team tf-spacing-2">
                    <div className="tf-container">
                        <div className="row rg-30">
                            <div className="col-lg-8">
                                {loading ? (
                                    <div className="text-center py-5">Loading your cart...</div>
                                ) : cartItems.length === 0 ? (
                                    <div className="cart-card text-center py-5">
                                        <h4>Your cart is empty</h4>
                                        <Link to="/item" className="tf-btn style-bg-primary mt-4">
                                            <span>Continue Shopping</span>
                                        </Link>
                                    </div>
                                ) : (
                                    cartItems.map((item) => {
                                        const itemKey = item.product_id || item.id;
                                        return (
                                            <div key={item.id} className={`cart-card ${updatingItems[itemKey] ? 'opacity-50' : ''}`}>
                                                <div className="row align-items-center gy-3">
                                                    <div className="col-4 col-md-2">
                                                        <img src={getImageUrl(item.image)} alt={item.title} className="cart-img" />
                                                    </div>
                                                    <div className="col-8 col-md-4">
                                                        <div className="cart-product-name">
                                                            <h6>{item.title}</h6>
                                                            <p className="mb-0 text-muted small">{item.brand} | {item.category}</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-6 col-md-2 price-text">
                                                        {currency}{Number(item.price).toLocaleString()}
                                                    </div>
                                                    <div className="col-6 col-md-2">
                                                        <div className="input-group quantity-group qty-box">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary qty-left-minus"
                                                                disabled={updatingItems[itemKey] || item.quantity <= 1}
                                                                onClick={() => handleQuantityChange(item, item.quantity - 1)}
                                                            >
                                                                <i className="fa fa-minus"></i>
                                                            </button>

                                                            <input
                                                                className="form-control text-center qty-input"
                                                                type="text"
                                                                name="quantity"
                                                                value={item.quantity}
                                                                readOnly
                                                            />

                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-secondary qty-right-plus"
                                                                disabled={updatingItems[itemKey]}
                                                                onClick={() => handleQuantityChange(item, item.quantity + 1)}
                                                            >
                                                                <i className="fa fa-plus"></i>
                                                            </button>
                                                        </div>
                                                    </div>
                                                    <div className="col-6 col-md-1 fw-bold">
                                                        {currency}{(Number(item.price) * item.quantity).toLocaleString()}
                                                    </div>
                                                    <div className="col-6 col-md-1 text-end">
                                                        <i
                                                            className={`fa fa-trash remove-icon ${updatingItems[itemKey] ? 'disabled' : ''}`}
                                                            style={{ cursor: 'pointer', color: '#ff4d4d' }}
                                                            onClick={() => !updatingItems[itemKey] && handleRemoveItem(item)}
                                                        ></i>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            <div className="col-lg-4">
                                <div className="summary-box">
                                    <h5 className="mb-4">Order Summary</h5>
                                    <div>
                                        <div className="sub-total">
                                            <div className="d-flex justify-content-between mb-2">
                                                <span>Subtotal</span>
                                                <span>{currency}{subtotal.toLocaleString()}</span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span>Tax ({taxRate}%)</span>
                                                <span>{currency}{tax.toLocaleString()}</span>
                                            </div>
                                        </div>
                                        <div className="d-flex justify-content-between fw-bold mb-4 border-top pt-3">
                                            <span>Total</span>
                                            <span style={{ color: 'var(--primary)' }}>{currency}{total.toLocaleString()}</span>
                                        </div>
                                    </div>
                                    <Link to="/checkout"
                                        className={`tf-btn style-bg-primary w-full justify-content-center mb-3 ${(loading || cartItems.length === 0) ? 'disabled' : ''}`}
                                        style={{ pointerEvents: (loading || cartItems.length === 0) ? 'none' : 'auto' }}
                                    >
                                        <i className="fa fa-credit-card mr-2"></i>
                                        <span>Proceed to Checkout</span>
                                    </Link>
                                    <Link to="/category" className="tf-btn style-border w-full justify-content-center">
                                        <span>Continue Shopping</span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default Cart;